/**
 * <product-video-gallery>
 * Slider Swiper 11 de vídeos verticais (autoplay mudo + toggle de som).
 * Toggle de som replica o padrão de sections/ss-shoppable-video.liquid (home).
 * Barra de progresso customizada (verde Alvya) — não usa módulo scrollbar do Swiper.
 */
if (!customElements.get('product-video-gallery')) {
  customElements.define(
    'product-video-gallery',
    class ProductVideoGallery extends HTMLElement {
      connectedCallback() {
        if (this._bound) return;
        this._bound = true;

        this.slider = this.querySelector('.product-video-gallery__slider');
        this.scrollbarEl = this.querySelector('.product-video-gallery__scrollbar');
        this.scrollbarDrag = this.querySelector('.product-video-gallery__scrollbar-drag');
        this.slides = this.querySelectorAll('.product-video-gallery__slide');
        this.dialog = this.querySelector('[data-pvg-dialog]');
        this.modalStage = this.querySelector('[data-pvg-modal-stage]');
        this.modalStatus = this.querySelector('[data-pvg-modal-status]');
        this.modalCloseButton = this.querySelector('[data-pvg-modal-close]');
        this.modalPreviousButton = this.querySelector('[data-pvg-modal-previous]');
        this.modalNextButton = this.querySelector('[data-pvg-modal-next]');
        this.openButtons = [...this.querySelectorAll('[data-pvg-open]')];

        this.bindModal();
        this.bindSoundToggle();
        this.observeAutoplay();
        this.deferSwiperInit();
      }

      disconnectedCallback() {
        this.io?.disconnect();
        this.swiperIO?.disconnect();
        this.swiper?.destroy(true, true);
        this.restoreModalVideo();
        if (this.dialog?.open) this.dialog.close();
        document.documentElement.classList.remove('pvg-modal-open');
      }

      bindModal() {
        if (!this.dialog || !this.modalStage || !this.openButtons.length) return;

        this.openButtons.forEach((button, index) => {
          button.addEventListener('click', () => {
            const requestedIndex = Number(button.dataset.pvgIndex);
            this.openModal(Number.isFinite(requestedIndex) ? requestedIndex : index, button);
          });
        });

        this.modalCloseButton?.addEventListener('click', () => this.closeModal());
        this.modalPreviousButton?.addEventListener('click', () => this.changeModalVideo(-1));
        this.modalNextButton?.addEventListener('click', () => this.changeModalVideo(1));

        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.closeModal();
        });

        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeModal();
        });

        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.changeModalVideo(-1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.changeModalVideo(1);
          }
        });

        this.modalStage.addEventListener('click', () => {
          if (this.didSwipe) {
            this.didSwipe = false;
            return;
          }
          if (!this.modalVideo) return;
          if (this.modalVideo.paused) {
            this.modalVideo.play()?.catch(() => {});
          } else {
            this.modalVideo.pause();
          }
        });

        this.modalStage.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' || this.swipePointerId != null) return;
          this.swipePointerId = event.pointerId;
          this.swipeStartX = event.clientX;
          this.swipeStartY = event.clientY;
          this.swipeStartedAt = performance.now();
          try {
            this.modalStage.setPointerCapture?.(event.pointerId);
          } catch (_error) {}
        });

        this.modalStage.addEventListener('pointerup', (event) => {
          if (event.pointerId !== this.swipePointerId) return;

          const deltaX = event.clientX - this.swipeStartX;
          const deltaY = event.clientY - this.swipeStartY;
          const elapsed = Math.max(performance.now() - this.swipeStartedAt, 1);
          const velocity = Math.abs(deltaX) / elapsed;
          const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY) * 1.2;

          if (isHorizontal && (Math.abs(deltaX) >= 48 || velocity > 0.35)) {
            this.didSwipe = true;
            this.changeModalVideo(deltaX < 0 ? 1 : -1);
          }

          this.swipePointerId = null;
        });

        this.modalStage.addEventListener('pointercancel', () => {
          this.swipePointerId = null;
        });
      }

      openModal(index, trigger) {
        if (!this.dialog || !this.modalStage || !this.slides.length) return;

        this.lastModalTrigger = trigger;
        this.modalOpen = true;
        this.io?.disconnect();
        this.querySelectorAll('.product-video-gallery__video').forEach((video) => video.pause());

        document.documentElement.classList.add('pvg-modal-open');
        if (!this.dialog.open) this.dialog.showModal();
        this.setModalVideo(index);
      }

      changeModalVideo(direction) {
        if (!this.modalOpen || this.slides.length < 2) return;
        this.setModalVideo(this.activeModalIndex + direction);
      }

      setModalVideo(index) {
        const total = this.slides.length;
        if (!total || !this.modalStage) return;

        const normalizedIndex = ((index % total) + total) % total;
        const slide = this.slides[normalizedIndex];
        const media = slide?.querySelector('[data-pvg-media]');
        const video = media?.querySelector('.product-video-gallery__video');
        if (!media || !video) return;

        this.restoreModalVideo();

        this.activeModalIndex = normalizedIndex;
        this.modalVideo = video;
        this.modalOrigin = media;
        this.modalAnchor = media.querySelector('[data-pvg-sound]');

        if (video.poster) media.style.backgroundImage = `url("${video.poster.replace(/"/g, '%22')}")`;
        media.classList.add('is-modal-active');

        video.pause();
        video.muted = false;
        video.controls = false;
        video.classList.add('product-video-gallery__video--modal');
        try {
          video.currentTime = 0;
        } catch (_error) {}

        this.modalStage.appendChild(video);
        if (this.modalStatus) this.modalStatus.textContent = `Vídeo ${normalizedIndex + 1} de ${total}`;

        window.requestAnimationFrame(() => video.play()?.catch(() => {}));
      }

      restoreModalVideo() {
        if (!this.modalVideo || !this.modalOrigin) return;

        const video = this.modalVideo;
        video.pause();
        video.muted = true;
        video.controls = false;
        video.classList.remove('product-video-gallery__video--modal');

        this.modalOrigin.insertBefore(video, this.modalAnchor || null);
        this.modalOrigin.classList.remove('is-modal-active');
        this.modalOrigin.style.removeProperty('background-image');

        const soundButton = this.modalOrigin.querySelector('[data-pvg-sound]');
        soundButton?.setAttribute('aria-pressed', 'false');
        soundButton?.setAttribute('aria-label', 'Ativar som');

        this.modalVideo = null;
        this.modalOrigin = null;
        this.modalAnchor = null;
      }

      closeModal() {
        if (!this.modalOpen && !this.modalVideo) return;

        const trigger = this.lastModalTrigger;
        this.restoreModalVideo();
        this.modalOpen = false;
        if (this.dialog?.open) this.dialog.close();
        document.documentElement.classList.remove('pvg-modal-open');
        this.observeAutoplay();
        trigger?.focus({ preventScroll: true });
      }

      // Swiper (bundle externo) só é carregado quando a galeria se aproxima do
      // viewport — autoplay e som (acima) funcionam sem ele. No Theme Editor
      // carrega imediato para a section responder na hora.
      deferSwiperInit() {
        const start = () =>
          this.loadSwiper()
            .then(() => this.initSwiper())
            .catch(() => {});

        if ((window.Shopify && window.Shopify.designMode) || !('IntersectionObserver' in window)) {
          start();
          return;
        }

        this.swiperIO = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.swiperIO.disconnect();
            this.swiperIO = null;
            start();
          },
          { rootMargin: '600px 0px' }
        );
        this.swiperIO.observe(this);
      }

      bindSoundToggle() {
        this.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          const btn = slide.querySelector('[data-pvg-sound]');
          if (!video || !btn) return;

          btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const turningOn = video.muted;

            this.querySelectorAll('.product-video-gallery__video').forEach((v) => {
              if (v !== video) v.muted = true;
            });
            this.querySelectorAll('[data-pvg-sound][aria-pressed="true"]').forEach((b) => {
              if (b !== btn) b.setAttribute('aria-pressed', 'false');
            });

            video.muted = !turningOn;
            btn.setAttribute('aria-pressed', turningOn ? 'true' : 'false');
            btn.setAttribute('aria-label', turningOn ? 'Desativar som' : 'Ativar som');
          });
        });
      }

      observeAutoplay() {
        const videos = [...this.querySelectorAll('.product-video-gallery__video')];
        if (!videos.length || !('IntersectionObserver' in window)) return;

        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target;
              if (entry.isIntersecting) {
                const playPromise = video.play();
                playPromise?.catch(() => {});
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.5 }
        );

        videos.forEach((video) => this.io.observe(video));
      }

      loadSwiper() {
        if (window.Swiper) return Promise.resolve();
        if (window.__pvgSwiperLoading) return window.__pvgSwiperLoading;

        const cssHref = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';
        if (!document.querySelector(`link[href="${cssHref}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = cssHref;
          document.head.appendChild(link);
        }

        window.__pvgSwiperLoading = new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });

        return window.__pvgSwiperLoading;
      }

      getSlidesPerView() {
        if (!this.swiper) return 1;
        const dynamic = this.swiper.slidesPerViewDynamic?.();
        if (typeof dynamic === 'number' && dynamic > 0) return dynamic;
        const configured = this.swiper.params.slidesPerView;
        return typeof configured === 'number' ? configured : 1;
      }

      getScrollbarColor() {
        const fromVar = getComputedStyle(this).getPropertyValue('--pvg-scrollbar-color').trim();
        return fromVar || '#a6cf54';
      }

      paintScrollbar() {
        if (!this.scrollbarEl || !this.scrollbarDrag) return;
        const color = this.getScrollbarColor();
        this.scrollbarEl.style.backgroundColor = 'rgba(18, 18, 18, 0.12)';
        this.scrollbarEl.style.height = '8px';
        this.scrollbarEl.style.minHeight = '8px';
        this.scrollbarEl.style.display = 'block';
        this.scrollbarEl.style.opacity = '1';
        this.scrollbarEl.style.visibility = 'visible';
        this.scrollbarDrag.style.backgroundColor = color;
        this.scrollbarDrag.style.height = '8px';
        this.scrollbarDrag.style.minHeight = '8px';
        this.scrollbarDrag.style.display = 'block';
        this.scrollbarDrag.style.opacity = '1';
        this.scrollbarDrag.style.visibility = 'visible';
      }

      updateScrollbar() {
        if (!this.swiper || !this.scrollbarDrag || !this.scrollbarEl) return;

        this.paintScrollbar();

        const slidesLength = this.swiper.slides.length;
        const perView = this.getSlidesPerView();
        const trackWidth = this.scrollbarEl.offsetWidth;

        if (!trackWidth || slidesLength <= perView) {
          this.scrollbarDrag.style.width = '100%';
          this.scrollbarDrag.style.transform = 'translate3d(0, 0, 0)';
          return;
        }

        const dragWidthPx = (perView / slidesLength) * trackWidth;
        const maxTranslate = trackWidth - dragWidthPx;
        const translateX = this.swiper.progress * maxTranslate;

        this.scrollbarDrag.style.width = `${dragWidthPx}px`;
        this.scrollbarDrag.style.transform = `translate3d(${translateX}px, 0, 0)`;
      }

      initSwiper() {
        if (this.swiper || !window.Swiper) return;

        this.swiper = new window.Swiper(this.slider, {
          speed: 300,
          spaceBetween: 8,
          watchOverflow: true,
          mousewheel: {
            forceToAxis: true,
            sensitivity: 1,
            releaseOnEdges: true,
          },
          breakpoints: {
            320: { spaceBetween: 8, slidesPerView: 2.5 },
            768: { spaceBetween: 8, slidesPerView: 2.5 },
            1024: { spaceBetween: 8, slidesPerView: 3 },
          },
          on: {
            init: () => this.updateScrollbar(),
            progress: () => this.updateScrollbar(),
            resize: () => this.updateScrollbar(),
            slideChange: () => this.updateScrollbar(),
            setTranslate: () => this.updateScrollbar(),
          },
        });

        window.requestAnimationFrame(() => this.updateScrollbar());
      }
    }
  );
}
